import { useAuthStore } from 'features/auth/stores/authStore';

import { apiClient } from '../client/apiClient';
import { ENDPOINTS } from '../config/endpoints';
import type { ApiResponse, PaginatedResponse } from '../types/common.types';
import type {
  CreateTimeEntryDto,
  RunningTimer,
  StartTimerDto,
  StopTimerDto,
  TimeEntry,
  TimeEntryFilters,
  TimeSummary,
  UpdateTimeEntryDto,
} from '../types/timeEntry.types';

const getWorkspaceId = (): string => {
  const workspaceId = useAuthStore.getState().user?.workspaceId;
  if (!workspaceId) throw new Error('No active workspace');
  return workspaceId;
};

export const timeEntryService = {
  getTimeEntries: (params?: TimeEntryFilters) => {
    const wid = getWorkspaceId();
    return apiClient.get<ApiResponse<PaginatedResponse<TimeEntry>>>(
      ENDPOINTS.TIME_ENTRIES.LIST(wid),
      { params },
    );
  },

  createTimeEntry: (data: CreateTimeEntryDto) => {
    const wid = getWorkspaceId();
    return apiClient.post<ApiResponse<{ id: string }>>(ENDPOINTS.TIME_ENTRIES.CREATE(wid), data);
  },

  updateTimeEntry: (id: string, data: UpdateTimeEntryDto) => {
    const wid = getWorkspaceId();
    return apiClient.put<ApiResponse<void>>(ENDPOINTS.TIME_ENTRIES.UPDATE(wid, id), data);
  },

  deleteTimeEntry: (id: string) => {
    const wid = getWorkspaceId();
    return apiClient.delete<ApiResponse<void>>(ENDPOINTS.TIME_ENTRIES.DELETE(wid, id));
  },

  getTimeSummary: () => {
    const wid = getWorkspaceId();
    return apiClient.get<ApiResponse<TimeSummary>>(ENDPOINTS.TIME_ENTRIES.SUMMARY(wid));
  },

  startTimer: (data: StartTimerDto) => {
    const wid = getWorkspaceId();
    return apiClient.post<ApiResponse<{ id: string }>>(ENDPOINTS.TIME_ENTRIES.START(wid), data);
  },

  stopTimer: (data: StopTimerDto) => {
    const wid = getWorkspaceId();
    return apiClient.post<ApiResponse<void>>(ENDPOINTS.TIME_ENTRIES.STOP(wid), data);
  },

  getRunningTimer: () => {
    const wid = getWorkspaceId();
    return apiClient.get<ApiResponse<RunningTimer | null>>(ENDPOINTS.TIME_ENTRIES.CURRENT(wid));
  },
};
